/**
 * CoverageMap - Phase 9.1 static SVG viewer.
 *
 * Renders a clean abstract-SVG view of zone coverage (concentric rings +
 * directional arc sectors) for a single site. No animation, no map tiles,
 * no live activity. Phase 9.2 will layer activity overlays; Phase 9.3 will
 * layer admin configuration; Phase 9.4 will layer geographic mode.
 *
 * Usage:
 *   import { CoverageMap } from '@/features/coverage-map';
 *   <CoverageMap zones={zones} boxes={[]} height={300} />
 *
 * Rendering rules (per DESIGN.md):
 *   - SVG viewBox 0 0 400 400; component fills its container.
 *   - Box marker: Tactical Gold ring + filled center, 8px radius.
 *   - Tier ring radii: inner = 30 + (sequence_order - 1) * 40; outer = inner + 40.
 *   - Compass bearing 0 = N; clockwise positive.
 *   - Omni zones -> full annulus.
 *   - Directional zones -> wedge centered on bearing with full width = beamwidth.
 *   - Fill = zone.color at alpha 0.35; stroke = zone.color at alpha 0.7, 1px.
 *   - Blend mode: 'screen' on dark theme, 'multiply' on light theme.
 *   - Faint guide circle + N/E/S/W compass labels outside the largest ring.
 *
 * Accessibility:
 *   - SVG has role="img" and aria-label="Coverage map: N zones across M boxes".
 *   - Each <path> has a <title> child for hover tooltip.
 *   - The component is theme-aware via data-theme on <html>; explicit theme
 *     prop overrides via data-theme on the component root.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  annularArcPath,
  directionalWedgePath,
  tierRadii,
  pointOnCircle,
} from './arc-geometry';
import * as styles from './CoverageMap.css';
import type {
  CoverageMapBox,
  CoverageMapProps,
  CoverageMapReplayState,
  CoverageMapZone,
} from './types';
import { dbmToColor } from './dbmToColor';
import { useReducedMotion } from '@/hooks/useReducedMotion';

/**
 * SVG viewBox dimensions. Square 400x400 keeps the math simple and matches
 * the design spec. The component scales to its container via width/height
 * props on the SVG element.
 */
const VIEWBOX_SIZE = 400;
const CENTER = VIEWBOX_SIZE / 2;

/**
 * Default arc opacity values. Fill is the wedge body (subtle), stroke is the
 * border (more legible). Per DESIGN.md sec 3.
 */
const ARC_FILL_ALPHA = 0.35;
const ARC_STROKE_ALPHA = 0.7;
const ARC_STROKE_WIDTH = 1;

/**
 * Resolve the active theme from props + DOM. Returns 'dark' or 'light'.
 *
 * When `themeProp === 'auto'` we read `data-theme` on `<html>` (matching the
 * SPA-wide ThemeProvider convention). The hook subscribes to attribute
 * changes so the component flips blend modes if the user toggles theme.
 *
 * @param themeProp Explicit theme prop value.
 * @returns Resolved theme.
 */
function useResolvedTheme(themeProp: 'auto' | 'dark' | 'light'): 'dark' | 'light' {
  const readDom = (): 'dark' | 'light' => {
    if (typeof document === 'undefined') return 'dark';
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'light' ? 'light' : 'dark';
  };
  const [resolved, setResolved] = useState<'dark' | 'light'>(() =>
    themeProp === 'auto' ? readDom() : themeProp,
  );
  useEffect(() => {
    if (themeProp !== 'auto') {
      // Explicit prop: reflect it into state. Cheap; harmless when stable.
      setResolved(themeProp);
      return;
    }
    // Auto mode: subscribe to <html data-theme> mutations so the component
    // re-renders if the user toggles theme. We do NOT seed state here
    // because useState's lazy initializer already read the DOM at mount;
    // an extra setResolved call on mount would trigger an unnecessary
    // re-render (and a noisy act() warning in tests).
    if (typeof document === 'undefined') return;
    const obs = new MutationObserver(() => setResolved(readDom()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => obs.disconnect();
  }, [themeProp]);
  return resolved;
}

/**
 * Build an aria-label string for the SVG. Uses the box count + zone count
 * so screen readers can convey the scope of the visualization.
 *
 * @param zoneCount Total zones rendered.
 * @param boxCount Total boxes rendered (defaults to 1 when implicit).
 * @returns Human-readable label.
 */
function buildAriaLabel(zoneCount: number, boxCount: number): string {
  const z = `${zoneCount} ${zoneCount === 1 ? 'zone' : 'zones'}`;
  const b = `${boxCount} ${boxCount === 1 ? 'box' : 'boxes'}`;
  return `Coverage map: ${z} across ${b}`;
}

/**
 * Build a per-arc tooltip string used by the <title> element.
 *
 * Format examples:
 *   "Zone Perimeter: omnidirectional"
 *   "Zone Approaching: 60deg beam at heading 90deg"
 */
function buildZoneTooltip(zone: CoverageMapZone): string {
  if (
    zone.zone_type === 'omni' ||
    zone.antenna_bearing_deg == null ||
    zone.antenna_beamwidth_deg == null
  ) {
    return `Zone ${zone.name}: omnidirectional`;
  }
  const bw = Math.round(zone.antenna_beamwidth_deg);
  const hd = Math.round(zone.antenna_bearing_deg);
  return `Zone ${zone.name}: ${bw}deg beam at heading ${hd}deg`;
}

/**
 * Resolve the SVG-coord (x, y) for a box's pinpoint. Boxes use unitless
 * (0, 1) positions; we map to SVG user units. Null position -> center.
 */
function boxPinPoint(box: CoverageMapBox): { x: number; y: number } {
  const pos = box.position;
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) {
    return { x: CENTER, y: CENTER };
  }
  return {
    x: Math.max(0, Math.min(VIEWBOX_SIZE, pos.x * VIEWBOX_SIZE)),
    y: Math.max(0, Math.min(VIEWBOX_SIZE, pos.y * VIEWBOX_SIZE)),
  };
}

/**
 * The largest outer radius across all rendered zones. Drives where the
 * compass labels + guide circle land.
 */
function maxOuterRadius(zones: CoverageMapZone[]): number {
  let max = 70; // tier 1 outer radius is the floor
  for (const z of zones) {
    const { outerR } = tierRadii(z.sequence_order);
    if (outerR > max) max = outerR;
  }
  return max;
}

/**
 * Per-zone arc renderer. Builds either a full annulus (omni) or a wedge
 * (directional). Returns null when the geometry collapses (e.g. zero
 * beamwidth on a directional zone).
 *
 * Phase 10 (Plan 10-02 T7): when `replayState` is provided, the fill is
 * resolved from `dbmToColor(rssiByZone.get(zone.id))` (falling back to
 * the upstream `zone.color` when no data is available for that zone).
 * When `zone.id === alertSector.zoneId`, the arc fill is locked to the
 * close-band red token at higher alpha; the brief flash CSS class is
 * applied while `Date.now() < flashUntil` AND not reduced-motion.
 */
function ZoneArc({
  zone,
  cx,
  cy,
  blendMode,
  replayState,
  reducedMotion,
}: {
  zone: CoverageMapZone;
  cx: number;
  cy: number;
  blendMode: 'screen' | 'multiply';
  replayState?: CoverageMapReplayState;
  reducedMotion: boolean;
}): JSX.Element | null {
  const { innerR, outerR } = tierRadii(zone.sequence_order);
  const isOmni =
    zone.zone_type === 'omni' ||
    zone.antenna_bearing_deg == null ||
    zone.antenna_beamwidth_deg == null;

  const d = isOmni
    ? annularArcPath(cx, cy, innerR, outerR, 0, 360)
    : directionalWedgePath(
        cx,
        cy,
        innerR,
        outerR,
        zone.antenna_bearing_deg ?? 0,
        zone.antenna_beamwidth_deg ?? 0,
      );

  if (!d) return null;

  // --- Phase 10 replay overlay ---
  const isAlertSector = replayState?.alertSector?.zoneId === zone.id;
  let fillColor = zone.color;
  let fillAlpha = ARC_FILL_ALPHA;
  if (replayState) {
    const rssi = replayState.rssiByZone.get(zone.id);
    if (rssi !== undefined && rssi !== null) {
      fillColor = dbmToColor(rssi);
    }
    if (isAlertSector) {
      // Lock alert sector to close-band red at higher alpha so it reads
      // as "the trigger" regardless of the live RSSI for the moment.
      fillColor = dbmToColor(-30); // close band -> status.alert via theme
      fillAlpha = 0.85;
    }
  }
  const flashWindow =
    isAlertSector && !reducedMotion
      ? typeof replayState?.alertSector?.flashUntil === 'number'
        ? replayState.alertSector.flashUntil
        : 0
      : 0;
  const flashing = flashWindow > 0 && Date.now() < flashWindow;
  const className = flashing ? `${styles.arc} ${styles.alertFlash}` : styles.arc;

  return (
    <path
      key={zone.id}
      className={className}
      d={d}
      fill={fillColor}
      fillOpacity={fillAlpha}
      stroke={fillColor}
      strokeOpacity={ARC_STROKE_ALPHA}
      strokeWidth={ARC_STROKE_WIDTH}
      fillRule="evenodd"
      style={{ mixBlendMode: blendMode }}
      data-zone-id={zone.id}
      data-zone-type={isOmni ? 'omni' : 'directional'}
      data-alert-sector={isAlertSector ? 'true' : undefined}
    >
      <title>{buildZoneTooltip(zone)}</title>
    </path>
  );
}

/**
 * Compute the centroid of a zone's arc footprint in SVG user units.
 *
 * For directional zones: midpoint of the wedge along the bearing at the
 * mean of inner/outer radii. For omni zones: at the bearing 0 mid-radius
 * (effectively a "north" anchor on the ring).
 *
 * Returns null when the zone has no usable bearing (omni without a
 * useful direction). Used to position the persistent red dot at the
 * alert sector during replay.
 */
function zoneCentroid(
  zone: CoverageMapZone,
  cx: number,
  cy: number,
): { x: number; y: number } | null {
  const { innerR, outerR } = tierRadii(zone.sequence_order);
  const midR = (innerR + outerR) / 2;
  const isOmni =
    zone.zone_type === 'omni' ||
    zone.antenna_bearing_deg == null ||
    zone.antenna_beamwidth_deg == null;
  const bearing = isOmni ? 0 : (zone.antenna_bearing_deg ?? 0);
  return pointOnCircle(cx, cy, midR, bearing);
}

/**
 * Box marker - Tactical Gold ring with a filled center.
 *
 * Phase 9.1 renders a non-interactive marker. Phase 9.3 will wire onBoxClick
 * to a button wrapper for configure mode.
 */
function BoxMarker({
  box,
  onBoxClick,
}: {
  box: CoverageMapBox;
  onBoxClick?: (boxId: string) => void;
}): JSX.Element {
  const { x, y } = boxPinPoint(box);
  const interactive = typeof onBoxClick === 'function';
  const ariaLabel = box.name ?? `Box ${box.id}`;
  return (
    <g
      data-testid="coverage-map-box-marker"
      data-box-id={box.id}
      role={interactive ? 'button' : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onBoxClick!(box.id) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onBoxClick!(box.id);
              }
            }
          : undefined
      }
      style={{ cursor: interactive ? 'pointer' : 'default' }}
    >
      <circle className={styles.boxRing} cx={x} cy={y} r={8} />
      <circle className={styles.boxCenter} cx={x} cy={y} r={3} />
    </g>
  );
}

/**
 * Compass labels (N / E / S / W) placed just outside the outermost ring.
 */
function CompassLabels({ outerR }: { outerR: number }): JSX.Element {
  const labelR = outerR + 18;
  const positions: Array<{ label: string; bearing: number }> = [
    { label: 'N', bearing: 0 },
    { label: 'E', bearing: 90 },
    { label: 'S', bearing: 180 },
    { label: 'W', bearing: 270 },
  ];
  return (
    <g aria-hidden="true">
      {positions.map(({ label, bearing }) => {
        const p = pointOnCircle(CENTER, CENTER, labelR, bearing);
        return (
          <text
            key={label}
            className={styles.compassLabel}
            x={p.x}
            y={p.y}
          >
            {label}
          </text>
        );
      })}
    </g>
  );
}

/**
 * Legend pill row. Skipped when showLegend is false.
 */
function Legend({ zones }: { zones: CoverageMapZone[] }): JSX.Element {
  return (
    <ul
      className={styles.legend}
      data-testid="coverage-map-legend"
      aria-label="Coverage map zone legend"
    >
      {zones.map((zone) => (
        <li
          key={zone.id}
          className={styles.legendPill}
          data-testid="coverage-map-legend-pill"
        >
          <span
            className={styles.legendSwatch}
            style={{ background: zone.color }}
            aria-hidden="true"
          />
          <span>{zone.name}</span>
          <span className={styles.legendTier}>tier {zone.sequence_order}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Public CoverageMap component. See module docstring + types.ts for the
 * full prop contract.
 */
export function CoverageMap(props: CoverageMapProps): JSX.Element {
  const {
    boxes,
    zones,
    width = '100%',
    height = 320,
    theme: themeProp = 'auto',
    onBoxClick,
    showLegend = true,
    className,
    'data-testid': testId,
    replayState,
  } = props;

  const resolvedTheme = useResolvedTheme(themeProp);
  const blendMode: 'screen' | 'multiply' =
    resolvedTheme === 'light' ? 'multiply' : 'screen';
  const reducedMotion = useReducedMotion();

  // Locate the alert sector zone so we can render the persistent dot at
  // its centroid. Only computed when replayState identifies an alert
  // sector AND the zone is in the rendered list.
  const alertSectorZone = useMemo<CoverageMapZone | null>(() => {
    if (!replayState?.alertSector) return null;
    return zones.find((z) => z.id === replayState.alertSector!.zoneId) ?? null;
  }, [replayState, zones]);

  // Phase 9.1 contract: when no boxes are passed, render a single implicit
  // box at canvas center. This is the v1 single-site default.
  const renderedBoxes: CoverageMapBox[] = useMemo(() => {
    if (boxes && boxes.length > 0) return boxes;
    return [{ id: '__implicit__', name: null, position: null }];
  }, [boxes]);

  const ariaLabel = buildAriaLabel(zones.length, renderedBoxes.length);
  const outerR = maxOuterRadius(zones);

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      data-testid={testId}
      data-theme={themeProp === 'auto' ? undefined : themeProp}
    >
      <div
        className={styles.canvas}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
        }}
      >
        <svg
          className={styles.svg}
          viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={ariaLabel}
          data-testid="coverage-map-svg"
          data-theme={resolvedTheme}
          data-blend-mode={blendMode}
        >
          {/* Faint guide circle at the outer edge of the largest tier */}
          <circle
            className={styles.guideCircle}
            cx={CENTER}
            cy={CENTER}
            r={outerR}
          />

          {/* Compass labels around the outer guide */}
          <CompassLabels outerR={outerR} />

          {/* Zones (arcs). Inside a group so blend modes compose cleanly. */}
          <g data-testid="coverage-map-arcs">
            {zones.map((zone) => (
              <ZoneArc
                key={zone.id}
                zone={zone}
                cx={CENTER}
                cy={CENTER}
                blendMode={blendMode}
                replayState={replayState}
                reducedMotion={reducedMotion}
              />
            ))}
          </g>

          {/* Box markers on top of arcs */}
          <g data-testid="coverage-map-boxes">
            {renderedBoxes.map((box) => (
              <BoxMarker
                key={box.id}
                box={box}
                onBoxClick={onBoxClick}
              />
            ))}
          </g>

          {/* Phase 10 alert-replay: persistent red dot at the alert sector centroid. */}
          {alertSectorZone &&
            (() => {
              const c = zoneCentroid(alertSectorZone, CENTER, CENTER);
              if (!c) return null;
              return (
                <circle
                  className={styles.alertDot}
                  cx={c.x}
                  cy={c.y}
                  r={6}
                  data-testid="coverage-map-alert-dot"
                  data-alert-zone-id={alertSectorZone.id}
                >
                  <title>
                    {`Alert at zone ${alertSectorZone.name} (chip ${
                      replayState?.alertSector?.chipId ?? ''
                    })`}
                  </title>
                </circle>
              );
            })()}
        </svg>
      </div>

      {showLegend && zones.length > 0 ? <Legend zones={zones} /> : null}
    </div>
  );
}
